/* event handlers, state, and state management for touch events */

import App from '../application-container';
import { Timeout } from '@phi/util/timeout';
import { pipe } from '@vaaas/fpts/function';
import { map } from '@vaaas/fpts/iter';
import {
    get_normalized_mouse_coordinates,
    handle_mousemove_while_clickdragging_vertex,
    handle_mousemove_while_drawing_edge_on_face_event,
    handle_mouseup_click_events,
    handle_mouseup_drag_end_events,
    handle_mouseup_nonclick_nondrag_events,
    handle_press_down_on_intersected_element,
} from './mouse';
import { average, from_pointer } from '@phi/maths/vector2';
import {
    is_click,
    is_pointer_normalised_prev_initialised,
    pointer_has_moved_a_lot,
    pointer_has_moved_a_little,
    pointer_normalised,
    pointer_normalised_prev,
    update_cursor_press_time_and_position,
} from '.';
import { Intersection, Vector2 } from 'three';
import SelectionBox from '../SelectionBox';
import { do_nothing } from '@phi/util';
import { uid_deselect_all_objects, add_halo_to_intersection_point } from '../ui_display';
import {
    find_camera_target_point_for_rotation,
    hide_rotation_center,
    reset_horizontal_vertical_rotate_direction,
} from '../utility';
import { set_button_states } from '../non_graphical_ui_actions';
import { hide_halo } from '../scene';
import {
    drag_clipping_plane,
    drag_face,
    find_intersection,
    handle_mousemove_while_clickdragging_edge,
    handle_mousemove_while_clickdragging_manipulator_element,
    pan,
    rotate,
    zoom,
} from './actions';
import { Unary } from '@vaaas/fpts/data';
import { long_press_sensitivity } from '@phi/constants';
import { indicate_long_press } from '@phi/touch';
import { set_shift } from '../keypress';
import { empty } from '@vaaas/fpts/map';
import { is_coordinate_plane } from '@features/indicate-axes';
import { is_symmetry_plane } from '@features/symmetry';
import { is_mirror_plane } from '@features/mirror';
import { clipping_on } from '@features/clipping';
import { get_core_side_dragging_state } from '@frontend/JCore/jcore';

//////////////////////////////
// STATE
//////////////////////////////

const PINCH_THRESHOLD = 56;

let primary = undefined as undefined | number;
let last_set_primary = -Infinity as number;

let intersection = null as Intersection | null;

const long_press_enabled = () =>
    !App.state.rotating
    && App.state.dragging_type === 'none'
    && !clipping_on.value;

const long_press_timeout = new Timeout(() => {
    long_press = active_fingers() === 1 && long_press_enabled();
    if (long_press) on_long_press();
}, long_press_sensitivity);

let long_press = false;

/** hashmap of pointer events
 *
 * each touch point emits its own pointer move event, so motions with two fingers send two events.
 * we record each touch point so we can average them for pan, use the distance for pinch, etc.
 */
export const touches = new Map<number, PointerEvent>();

/** keeps track of the distance between two touches, for pinch zooming */
let distance_between_touches = 0;
let distance_between_touches_prev = 0;
let distance_between_touches_initial = 0;

/** mouse move events are emitted one by one, one for each finger
 *
 * this means that if two fingers are down and they are panned,
 * the move event is emitted twice
 *
 * we keep a counter of the number of events encountered,
 * and when we reach zero we can know we have a complete motion
 */
let expecting = 0;

/** pointer to function that does the actual handling of drag events */
let move_handler: Unary<PointerEvent, void> = do_nothing;

//////////////////////////////
// STATE HANDLERS
//////////////////////////////

function update_distance_between_touches() {
    if (active_fingers() === 2) {
        const [one, two] = Array.from(touches.values())
            .map(from_pointer) as [Vector2, Vector2];
        const d = one.distanceTo(two);
        if (distance_between_touches_prev === 0)
            distance_between_touches_initial = distance_between_touches_prev = d;
        else
            distance_between_touches_prev = distance_between_touches;
        distance_between_touches = d;
    } else {
        distance_between_touches = distance_between_touches_prev = distance_between_touches_initial = 0;
    }
}

/** positive = distance increasing, zoom in
 *
 * negative = distance decreasing, zoom out
 */
function pinch_diff() {
    return distance_between_touches - distance_between_touches_prev;
}

const has_pinched_enough_for_zoom = () => Math.abs(distance_between_touches - distance_between_touches_initial) > PINCH_THRESHOLD;

function update_pointer_normalised(es: Iterable<PointerEvent>) {
    const fresh = pipe(
        es,
        map(get_normalized_mouse_coordinates),
        average,
    );

    if (is_pointer_normalised_prev_initialised()) {
        pointer_normalised_prev.x = pointer_normalised.x;
        pointer_normalised_prev.y = pointer_normalised.y;
    } else {
        pointer_normalised_prev.x = fresh.x;
        pointer_normalised_prev.y = fresh.y;
    }

    pointer_normalised.x = fresh.x;
    pointer_normalised.y = fresh.y;
}

export function push_event(e: PointerEvent) {
    if (!touches.has(e.pointerId)) {
        long_press = false;
        if (active_fingers() === 0)
            long_press_timeout.start();
        else
            long_press_timeout.stop();
    }
    touches.set(e.pointerId, e);
}

export function pop_event(e: PointerEvent) {
    touches.delete(e.pointerId);
    long_press_timeout.stop();
}

export function active_fingers() {
    return touches.size;
}

/** returns whether the user has touched "empty" space that isn't a phi geometry
 *
 * is true for no intersection, but some intersections have special handling, like:
 *
 * - symmetry planes
 * - coordinate planes
 * - mirror planes
 */
export function touched_the_air(intersection: Intersection | null) {
    if (intersection === null) return true;
    else if (is_symmetry_plane(intersection.object))
        return App.state.symmetry_plane_selected;
    else if (is_mirror_plane(intersection.object))
        return App.state.mirror_plane_selected;
    else if (is_coordinate_plane(intersection.object))
        return true;
    else
        return false;
}

//////////////////////////////
// EVENT HANDLERS
//////////////////////////////

export function update_selection_box() {
    uid_selection_box.update(null, pointer_normalised);
    uid_selection_box.select_objects_in_box();
}

export function regular_edge_drag() {
    handle_mousemove_while_clickdragging_edge(pointer_normalised, false);
}

export function elevator_edge_drag() {
    handle_mousemove_while_clickdragging_edge(pointer_normalised, true);
}

export function regular_vertex_drag() {
    handle_mousemove_while_clickdragging_vertex(pointer_normalised, false);
}

export function elevator_vertex_drag() {
    handle_mousemove_while_clickdragging_vertex(pointer_normalised, true);
}

export function handle_drag_face() {
    drag_face(pointer_normalised);
}

export function handle_dragging_manipulator_element() {
    handle_mousemove_while_clickdragging_manipulator_element(long_press);
}

export function handle_moving_mouse_while_drawing() {
    handle_mousemove_while_drawing_edge_on_face_event();
}

export function handle_pan() {
    pan();
    if (has_pinched_enough_for_zoom())
        move_handler = handle_pan_pinch;
}

export function handle_pan_pinch() {
    pan();
    zoom(pinch_diff() * 0.005);
}

export function touch_move(e: PointerEvent) {
    push_event(e);
    expecting--;
    if (expecting <= 0) {
        expecting = active_fingers();
        update_pointer_normalised(touches.values());
        uid_raycaster.setFromCamera(pointer_normalised, uid_camera);
        update_distance_between_touches();
    } else {
        return;
    }

    if (move_handler !== do_nothing)
        move_handler(e);
    else switch (active_fingers()) {
        // single finger cases
        case 1: {
            if (!pointer_has_moved_a_little(e))
                break;
            long_press_timeout.stop();

            if (pointer_has_moved_a_lot(e)) {
                if (App.state.drawing_edge || App.state.carving) {
                    move_handler = handle_moving_mouse_while_drawing;
                    move_handler(e);
                } else if (touched_the_air(intersection)) {
                    // touching "the air"
                    // should start rotating
                    hide_halo();
                    App.state.rotating = true;
                    move_handler = rotate;
                    move_handler(e);
                } else if (clipping_on.value) {
                    move_handler = drag_clipping_plane;
                    move_handler(e);
                }
            } else if (uid_state.manipulator_object_was_clicked) {
                move_handler = handle_dragging_manipulator_element;
                move_handler(e);
            } else if (uid_state.draggable_object_was_clicked) {
                // Check that we're not in a special state
                if (
                    !App.state.adding_vertex
                    && !uid_state.adding_edge_endpoints_status
                    && !App.state.offset_face_selecting_vertex
                ) {
                    // The user is click-dragging a draggable object.
                    if (uid_state.in_first_drag_step && uid_drag_object.type != 'face')
                        uid_deselect_all_objects();
                    if (uid_drag_object.type == 'vertex') {
                        move_handler =
                            long_press
                                ? elevator_vertex_drag
                                : regular_vertex_drag;
                        move_handler(e);
                    } else if (uid_drag_object.type == 'edge') {
                        move_handler =
                            long_press
                                ? elevator_edge_drag
                                : regular_edge_drag;
                        move_handler(e);
                    } else if (uid_drag_object.type == 'face') {
                        move_handler = handle_drag_face;
                        move_handler(e);
                    } else {
                        // TODO
                        // In the future we can also handle the following cases:
                        // - The drag object is a vertex, but an adjacent edge was selected, so it's drag vertex on edge.
                        // - The drag object is a vertex or edge, but more than one objects were selected, so it's drag set.
                    }
                    uid_state.in_first_drag_step = false;
                }
            }

            break;
        }

        // double finger cases
        case 2: {
            if (move_handler === do_nothing) {
                move_handler = handle_pan;
                move_handler(e);
            }
            break;
        }

        default:
            move_handler = do_nothing;
            break;
    }
}

export function touch_down(e: PointerEvent) {
    if (e.isPrimary) {
        if (primary === undefined) {
            primary = e.pointerId;
            last_set_primary = Date.now();
        } else if (primary !== e.pointerId) {
            const now = Date.now();
            if (now - last_set_primary >= 100)
                empty(touches);
            primary = e.pointerId;
            last_set_primary = now;
        }
    }
    expecting++;
    push_event(e);
    update_pointer_normalised(touches.values());
    set_shift(false);
    move_handler = do_nothing;

    switch (active_fingers()) {
        case 1: {
            // Prepare and raycaster and camera, and find intersections.
            intersection = find_intersection();

            // Reset the mouseDown time (we do it after raycasting which may take a long time)
            update_cursor_press_time_and_position(e);

            // Also identify the camera target point (but let's not show it on mobile, too much clutter)
            uid_state.camera_target_point = find_camera_target_point_for_rotation(uid_camera);

            if (intersection &&
                !(App.state.offset_face_selecting_vertex || App.state.offset_face_dragging))
                // If we're in the offset process we don't handle the mousedown event, we'll let the click event handler
                // do what it needs to do.
                handle_press_down_on_intersected_element(intersection, true, long_press);

            break;
        }

        default: break;
    }
}

const is_dragging = () => uid_in_dragging_mode || (
    get_core_side_dragging_state() !== 'off'
    && get_core_side_dragging_state() !== 'face_offset'
);


function cleanup() {
    hide_halo();
    // Set global flags to correct values. We can do this regardless of what was happening
    // while touch was active.
    uid_state.manipulator_object_was_clicked = false;
    uid_state.draggable_object_was_clicked = false;
    uid_state.right_mouse_button_is_pressed = false;
    uid_state.middle_mouse_button_is_pressed = false;
    App.state.rotating = false;
    App.state.panning = false;
    App.state.rotating = false;
    uid_state.in_first_drag_step = true;
    if (uid_selection_box)
        uid_selection_box.remove();

    hide_rotation_center();
    move_handler = do_nothing;
}

export function touch_up(e: PointerEvent) {
    App.state.debug_text = '';

    reset_horizontal_vertical_rotate_direction();

    switch (active_fingers()) {
        case 1: {
            const finished_rotation = App.state.rotating;
            cleanup();
            if (long_press && is_click(e, true) && !is_dragging()) {
                set_shift(true); // TODO we should not be setting global state like this
                handle_mouseup_click_events(true);
                set_button_states();
            } else if (is_click(e, false) && !is_dragging()) {
                handle_mouseup_click_events(false);
                set_button_states();
            } else if (uid_in_dragging_mode || (uid_raynode == uid_manip_raynode && get_core_side_dragging_state() != 'off')) {
                handle_mouseup_drag_end_events(e);
                set_button_states();
            } else {
                handle_mouseup_nonclick_nondrag_events(finished_rotation);
            }

            break;
        }

        case 2:
            distance_between_touches = distance_between_touches_prev = 0;
            break;

        default:
            cleanup();
            break;
    }

    // cleanup
    long_press = false;
    set_shift(false);

    expecting = Math.max(expecting - 1, 0);
    pop_event(e);
    update_pointer_normalised(touches.values());
}

export function on_long_press() {
    if (active_fingers() !== 1) return;

    indicate_long_press();

    if (touched_the_air(intersection)) {
        uid_selection_box = new SelectionBox();
        uid_selection_box.update(pointer_normalised, null);
        move_handler = update_selection_box;
    } else if (intersection) {
        drag_start_movement_direction = 'halo';
        add_halo_to_intersection_point(intersection, pointer_normalised);
    }
}
